import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchShippingRangeLabel } from "../lib/api";

const EMAIL = "support.millionthreads@gmail.com";
const EmailLink = () => (
  <a href={`mailto:${EMAIL}`} style={{ color: "var(--mt-text)", textDecoration: "underline", opacity: 0.8 }}>{EMAIL}</a>
);

function LegalLayout({ title, children }) {
  return (
    <section className="page-first-section" style={{ paddingBottom: "clamp(3rem,8vw,8rem)" }}>
      <div className="mt-container" style={{ maxWidth: 760 }}>
        <p className="font-mono text-[0.65rem] tracking-mt uppercase mb-3" style={{ color: "var(--mt-text-muted)" }}>
          Legal · Million Threads
        </p>
        <h1 className="font-display font-extralight leading-tight mb-2" style={{ fontSize: "clamp(2rem,5vw,3.2rem)", color: "var(--mt-text)" }}>
          {title}
        </h1>
        <p className="font-mono text-[0.62rem] tracking-mt uppercase mb-10" style={{ color: "var(--mt-text-muted)" }}>
          millionthreads.store
        </p>
        <div className="space-y-8" style={{ color: "var(--mt-text)" }}>
          {children}
        </div>
        <div className="mt-14 pt-8 border-t" style={{ borderColor: "var(--mt-glass-border)" }}>
          <p className="font-mono text-[0.62rem] tracking-mt uppercase mb-4" style={{ color: "var(--mt-text-muted)" }}>Other policies</p>
          <div className="flex flex-wrap gap-4">
            {[["Terms & Conditions", "/terms"], ["Privacy Policy", "/privacy"], ["Refund & Return Policy", "/refund"], ["Shipping Policy", "/shipping"]].map(([label, path]) => (
              <Link key={path} to={path} className="font-mono text-[0.65rem] tracking-mt uppercase underline opacity-60 hover:opacity-100 transition-opacity" style={{ color: "var(--mt-text)" }}>{label}</Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display font-light mb-3" style={{ fontSize: "1.15rem", color: "var(--mt-text)" }}>{title}</h2>
      <div className="space-y-3 font-body text-[0.92rem] leading-relaxed" style={{ color: "var(--mt-text-muted)" }}>
        {children}
      </div>
    </div>
  );
}

function P({ children }) {
  return <p>{children}</p>;
}

function UL({ items }) {
  return (
    <ul className="space-y-1.5 pl-4">
      {items.map((item, i) => (
        <li key={i} style={{ listStyleType: "disc" }}>{item}</li>
      ))}
    </ul>
  );
}

// ─── TERMS & CONDITIONS ───────────────────────────────────────────────────────
function Terms() {
  return (
    <LegalLayout title="Terms & Conditions">
      <Section title="1. About Us">
        <P>Million Threads is an independent premium streetwear brand. Our products are sold through millionthreads.store and select other platforms. For any queries, contact us at <EmailLink />.</P>
      </Section>

      <Section title="2. Products">
        <P>All products are limited-edition drops. We make every effort to ensure descriptions, images, and pricing are accurate. We reserve the right to correct errors or omissions at any time without prior notice.</P>
        <P>Product colours may appear slightly different on screen depending on your display. Minor colour variations between the image and the physical product are not grounds for a return.</P>
      </Section>

      <Section title="3. Orders & Payment">
        <P>By placing an order, you confirm that all information provided is accurate and complete. We reserve the right to refuse or cancel any order at our discretion, including orders that appear fraudulent, contain incorrect pricing, or cannot be fulfilled.</P>
        <P>There is no minimum order value. Payment is processed securely through Razorpay. We accept UPI, debit and credit cards, net banking, and popular wallets. Your payment information is never stored on our servers.</P>
        <P>Once payment is confirmed, you will receive an order confirmation at the email address provided at checkout.</P>
      </Section>

      <Section title="4. Order Cancellation">
        <P>Orders may be cancelled before they are dispatched. Once an order has been shipped, it cannot be cancelled. To request a cancellation, email <EmailLink /> immediately with your order number.</P>
        <P>If your cancellation request is received before dispatch, a full refund will be issued to your original payment method.</P>
      </Section>

      <Section title="5. Pricing">
        <P>All prices are listed in Indian Rupees (INR) and are inclusive of GST unless stated otherwise. Prices are subject to change without notice. The price at the time of your order is the price you pay.</P>
      </Section>

      <Section title="6. Delivery">
        <P>We ship Pan-India. There are no restrictions on who may receive a delivery at the provided address. We are not responsible for failed deliveries due to an incorrect address or unavailability of anyone to receive the package.</P>
      </Section>

      <Section title="7. Intellectual Property">
        <P>All content on millionthreads.store, including product designs, images, text, logos, and graphics, is the exclusive property of Million Threads and is protected under applicable intellectual property laws. You may not reproduce, distribute, or use any content from this site without prior written permission.</P>
      </Section>

      <Section title="8. Limitation of Liability">
        <P>To the fullest extent permitted by law, Million Threads shall not be liable for any indirect, incidental, or consequential damages arising from your use of our site or products. Our total liability for any claim shall not exceed the amount you paid for the specific order in question.</P>
      </Section>

      <Section title="9. Governing Law">
        <P>These Terms are governed by the laws of India. Any disputes arising from these Terms or your use of this site shall be subject to the exclusive jurisdiction of the courts of Kolkata, West Bengal.</P>
      </Section>

      <Section title="10. Changes to These Terms">
        <P>We reserve the right to update these Terms at any time. The updated version will be posted on this page. Continued use of the site after changes constitutes your acceptance of the updated Terms.</P>
      </Section>

      <Section title="11. Contact">
        <P>For any questions regarding these Terms: <EmailLink /></P>
      </Section>
    </LegalLayout>
  );
}

// ─── PRIVACY POLICY ───────────────────────────────────────────────────────────
function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <Section title="1. Information We Collect">
        <P>When you place an order or create an account, we collect:</P>
        <UL items={[
          "Your name and email address",
          "Your phone number and delivery address",
          "Payment information. This is processed directly and securely by Razorpay; we never see or store your card details",
          "Order history",
        ]} />
        <P>When you browse our site, we may automatically collect basic technical information such as your browser type and pages visited, to help us improve the site.</P>
      </Section>

      <Section title="2. Signing In With Google">
        <P>Million Threads offers the option to create an account or sign in using Google Sign-In. If you choose this option, here is exactly what we access, how we use it, and how you can remove it.</P>
        <P>Data accessed: when you sign in with Google, we receive your name, email address, and profile picture from your Google account. We do not request or receive access to your Gmail, Google Drive, contacts, calendar, or any other Google data beyond this basic profile information.</P>
        <P>Data usage: we use this information solely to create and authenticate your Million Threads account, to display your name and profile picture within your account area, and to send you order-related communication to the email address provided by Google.</P>
        <P>Data sharing: information obtained through Google Sign-In is not shared with any third party. It is used exclusively to operate your Million Threads account and is stored in the same secure database as all other account information.</P>
        <P>Data storage and protection: your Google account data is stored securely using Supabase, with the same industry-standard security practices described in the Data Storage and Security section below. Access is restricted to what is required to operate your account.</P>
        <P>Data retention and deletion: we retain the data obtained from Google Sign-In for as long as your Million Threads account remains active. To request deletion of this data or your entire account, email <EmailLink /> and we will process your request within a reasonable timeframe.</P>
      </Section>

      <Section title="3. How We Use Your Information">
        <P>We use your information to:</P>
        <UL items={[
          "Process and fulfil your orders",
          "Send you order confirmations and delivery updates",
          "Respond to your support queries",
          "Send you updates about new drops and offers, only if you have opted in to marketing communications",
        ]} />
        <P>We do not use your data for any purpose beyond what is listed above without your explicit consent.</P>
      </Section>

      <Section title="4. Sharing Your Information">
        <P>We do not sell or trade your personal information. We share your data only with:</P>
        <UL items={[
          "Razorpay, to process your payment. Razorpay receives your payment information directly during checkout as part of their payment infrastructure.",
          "Delivery partners. We share only the information necessary to fulfil your order: your name, delivery address, and phone number.",
        ]} />
        <P>We do not currently run paid advertising and do not share your data with any advertising or analytics platforms.</P>
      </Section>

      <Section title="5. Data Storage & Security">
        <P>Your data is stored securely using Supabase, a cloud database provider with industry-standard security practices. We take reasonable steps to protect your information against unauthorised access or disclosure. No method of internet transmission is 100% secure, and we cannot guarantee absolute security.</P>
      </Section>

      <Section title="6. Data Retention & Deletion">
        <P>We retain your personal data, including data obtained via Google Sign-In, for as long as your account remains active or as needed to fulfil the purposes described in this policy, including resolving disputes and enforcing our agreements.</P>
        <P>You may request deletion of your account and associated data at any time by emailing <EmailLink />. We will process deletion requests within a reasonable timeframe, except where we are required to retain certain information for legal, tax, or accounting purposes.</P>
      </Section>

      <Section title="7. Cookies">
        <P>We use cookies to maintain your session and preferences on the site. You can disable cookies through your browser settings, though this may affect certain site functionality such as staying logged in.</P>
      </Section>

      <Section title="8. Your Rights">
        <P>You have the right to access, correct, or request deletion of your personal data. To exercise any of these rights, email us at <EmailLink />. We will respond within a reasonable timeframe.</P>
      </Section>

      <Section title="9. Changes to This Policy">
        <P>We may update this Privacy Policy from time to time. The updated version will be posted on this page. Continued use of the site after changes constitutes acceptance of the updated policy.</P>
      </Section>

      <Section title="10. Contact">
        <P>For any privacy-related questions: <EmailLink /></P>
      </Section>
    </LegalLayout>
  );
}

// ─── REFUND & RETURN POLICY ──────────────────────────────────────────────────
function Refund() {
  return (
    <LegalLayout title="Refund & Return Policy">
      <Section title="1. Our Policy">
        <P>Every piece from Million Threads is made with care. We stand behind our quality. Returns and refunds are accepted only in specific circumstances as detailed below. Please read this policy before placing your order.</P>
      </Section>

      <Section title="2. When We Accept Returns">
        <P>We accept return requests only in the following cases:</P>
        <UL items={[
          "The product has a confirmed manufacturing defect: stitching failure, print error, or fabric fault present at the time of delivery",
          "The wrong item was delivered: wrong product, wrong size, or wrong colour compared to what was ordered",
        ]} />
        <P>We do not accept returns for change of mind, personal preference, or incorrect size selection by the customer. All sales are otherwise final.</P>
      </Section>

      <Section title="3. How to Raise a Return Request">
        <P>If your order qualifies for a return:</P>
        <UL items={[
          `Email ${EMAIL} within 7 days of receiving your order`,
          "Use the subject line: RETURN REQUEST [Your Order Number]",
          "Attach clear photographs showing the defect or incorrect item",
          "Include your order number, name, and a brief description of the issue",
        ]} />
        <P>We review all requests manually before approving. Requests without photographic proof will not be processed. We reserve the right to reject requests where the damage appears to be caused intentionally or through misuse. This policy is in place to protect against staged returns by customers attempting to use this as a workaround for change-of-mind situations where no refund is available.</P>
      </Section>

      <Section title="4. Return Shipping">
        <P>If your return request is approved, we will provide you with the return shipping address. Return shipping costs are handled as follows:</P>
        <UL items={[
          "Manufacturing defect or wrong item sent: we cover the cost of return shipping",
          "All other approved returns: the customer is responsible for return shipping",
        ]} />
        <P>The refund will only be processed after the item is received by us and physically inspected to verify the stated defect or error.</P>
      </Section>

      <Section title="5. Refunds on Approved Returns">
        <P>Once we receive and inspect the returned item and confirm the issue:</P>
        <UL items={[
          "A refund of 90-95% of the order value will be issued (a convenience and processing fee of 5-10% is deducted to cover payment gateway charges and handling)",
          "You may choose to receive the refund to your original payment method, or as store credit",
          "Refunds are typically processed within 7-10 business days of inspection",
        ]} />
      </Section>

      <Section title="6. Exchanges">
        <P>We accept exchange requests within 7 days of receiving your order, for a different size or a different design.</P>
        <P>Exchange process:</P>
        <UL items={[
          `Email ${EMAIL} with your order number and the item you would like to exchange for`,
          "Once confirmed, ship your item back to us, unworn, unwashed, and with all original tags intact",
          "We inspect the returned item upon receipt",
          "We then ship the exchanged item to you",
        ]} />
        <P>Pricing on exchanges:</P>
        <UL items={[
          "If the exchanged item is higher priced, you pay the difference",
          "If the exchanged item is lower priced, the difference is issued as store credit",
        ]} />
        <P>Shipping on exchanges:</P>
        <UL items={[
          "Size exchange: we cover the cost of shipping the exchanged item back to you. You cover the cost of shipping the original item back to us.",
          "Design exchange: the customer covers shipping both ways",
        ]} />
      </Section>

      <Section title="7. Non-Returnable Conditions">
        <P>A return or exchange will not be accepted if:</P>
        <UL items={[
          "The request is made after 7 days from the date of delivery",
          "The item shows signs of wear, washing, or alteration",
          "Original tags have been removed",
          "The damage is caused by misuse, neglect, or intentional modification",
          "Photographic proof is not provided with the request",
        ]} />
      </Section>

      <Section title="8. Contact">
        <P>For all return, refund, and exchange queries: <EmailLink /></P>
      </Section>
    </LegalLayout>
  );
}

// ─── SHIPPING POLICY ─────────────────────────────────────────────────────────
function Shipping() {
  const [shipRange, setShipRange] = useState("");
  useEffect(() => {
    let cancelled = false;
    fetchShippingRangeLabel().then((v) => { if (!cancelled) setShipRange(v); });
    return () => { cancelled = true; };
  }, []);
  return (
    <LegalLayout title="Shipping Policy">
      <Section title="1. Where We Ship">
        <P>We ship Pan-India, across all states and union territories of India. We do not offer international shipping at this time.</P>
      </Section>

      <Section title="2. Shipping Charges">
        <P>Shipping is calculated based on your delivery location and shown at checkout before you pay. {shipRange ? `Rates range from ${shipRange.replace("–", " to ")} depending on the state you're shipping to, closer regions cost less, farther regions cost more.` : "Rates vary by state, closer regions cost less, farther regions cost more."}</P>
        <P>Shipping is free when a promo code that includes free shipping is applied at checkout.</P>
      </Section>

      <Section title="3. Order Processing Time">
        <P>Orders are processed within 2-3 business days of payment confirmation. Business days exclude Sundays and public holidays. You will receive a notification with tracking details once your order is dispatched.</P>
      </Section>

      <Section title="4. Estimated Delivery Time">
        <P>Delivery timelines after dispatch vary by location. On average, expect delivery within 5-14 days from the date of dispatch:</P>
        <UL items={[
          "Metro cities (Mumbai, Delhi, Bengaluru, Kolkata, Hyderabad, Chennai): 3-5 business days",
          "Tier 2 and Tier 3 cities: 5-8 business days",
          "Remote areas and northeastern states: 7-14 business days",
        ]} />
        <P>These are estimates and may vary due to courier delays, weather, public holidays, or other factors outside our control.</P>
      </Section>

      <Section title="5. Courier Partners">
        <P>We work with multiple courier partners and select the most appropriate one based on your delivery location and availability at the time of dispatch. You will receive tracking information specific to the courier handling your order.</P>
      </Section>

      <Section title="6. Order Tracking">
        <P>Once your order is dispatched, you will receive a tracking number via email or WhatsApp. Use this to track your shipment directly on the courier partner's website.</P>
      </Section>

      <Section title="7. Failed Delivery Attempts">
        <P>If a delivery attempt fails due to an incorrect address, unavailability of anyone to receive the package, or refusal to accept, the courier may attempt redelivery or return the package to us. We are not responsible for additional costs arising from failed deliveries. Please ensure your address and contact number are correct at checkout.</P>
      </Section>

      <Section title="8. Lost Shipments">
        <P>If your order appears to be genuinely lost in transit, contact us at <EmailLink /> within 48 hours of the expected delivery date with your order number. We will coordinate with the courier and, if the shipment is confirmed lost, reship your order at no additional cost to you.</P>
      </Section>

      <Section title="9. Pre-Order Shipping">
        <P>For items listed as Pre-Order, the estimated dispatch timeline will be communicated to you via WhatsApp or email at the time of purchase. We will notify you of any significant changes to the timeline before dispatch.</P>
      </Section>

      <Section title="10. Contact">
        <P>For all shipping queries: <EmailLink /></P>
      </Section>
    </LegalLayout>
  );
}

// ─── ROUTER ──────────────────────────────────────────────────────────────────
export default function Legal() {
  const path = window.location.pathname;
  if (path === "/privacy") return <Privacy />;
  if (path === "/refund") return <Refund />;
  if (path === "/shipping") return <Shipping />;
  return <Terms />;
}
